export const CLASS_STATUSES = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  ARCHIVED: 'archived'
} as const;

export const CLASS_GRADES = [
  'K1', 'K2', 'K3', // Kindergarten
  '1', '2', '3', '4', '5', // Elementary
  '6', '7', '8', '9', // Middle School
  '10', '11', '12' // High School
] as const;

export const formatGrade = (grade: string): string =>
  grade.startsWith('K') ? `Mẫu giáo ${grade}` : `Khối ${grade}`;

export const GRADE_OPTIONS = CLASS_GRADES.map((grade) => ({ value: grade, label: formatGrade(grade) }));

// Năm học mới tính từ tháng 8: trường xếp lớp cho năm mới từ trước ngày khai giảng.
const SCHOOL_YEAR_START_MONTH = 7;

const toSchoolYear = (startYear: number) => `${startYear}-${startYear + 1}`;

export const getCurrentSchoolYear = (date: Date = new Date()): string => {
  const year = date.getFullYear();
  return toSchoolYear(date.getMonth() >= SCHOOL_YEAR_START_MONTH ? year : year - 1);
};

/** Các năm học quanh năm hiện tại, tăng dần: `past` năm trước và `future` năm sau. */
export const getSchoolYears = (date: Date = new Date(), past = 5, future = 1): string[] => {
  const currentStart = Number(getCurrentSchoolYear(date).slice(0, 4));
  return Array.from({ length: past + future + 1 }, (_, i) => toSchoolYear(currentStart - past + i));
};

/** Dữ liệu cũ lưu mỗi năm đầu ("2023") → quy về "2023-2024". */
export const normalizeSchoolYear = (value?: string | number | null): string => {
  const text = value == null ? '' : String(value).trim();
  return /^\d{4}$/.test(text) ? toSchoolYear(Number(text)) : text;
};

/**
 * Các giá trị có thể đang lưu cho một năm học. Dữ liệu cũ lưu năm đầu dạng chuỗi ("2023")
 * hoặc số (2023); Mongo so khớp theo kiểu nên phải gửi cả hai.
 */
export const getSchoolYearAliases = (schoolYear: string): (string | number)[] => {
  if (!/^\d{4}-\d{4}$/.test(schoolYear)) return [schoolYear];
  const startYear = schoolYear.slice(0, 4);
  return [schoolYear, startYear, Number(startYear)];
};

export const SCHOOL_YEARS = getSchoolYears();

// Lớp vẫn giữ teacherId khi giáo viên bị xoá hoặc rời trường → nói rõ, không để lẫn với lớp chưa phân công.
export const MISSING_TEACHER_LABEL = 'Giáo viên không còn trong trường';
// Lớp chưa có chủ nhiệm (vd. tạo tự động khi import học sinh). Mọi màn đọc chung hằng này.
export const NO_TEACHER_LABEL = 'Chưa phân công';

/** Nhãn chủ nhiệm của lớp: tên nếu tra được, còn id mà không ra tên → giáo viên đã rời trường, không có id → chưa phân công. */
export const homeroomTeacherLabel = (teacherId?: string | null, teacherName?: string | null): string =>
  teacherName || (teacherId ? MISSING_TEACHER_LABEL : NO_TEACHER_LABEL);

export const MAX_STUDENTS_PER_CLASS = 50;
export const MIN_STUDENTS_PER_CLASS = 1;

export const CLASS_TABLE_COLUMNS = [
  { key: 'code', label: 'Mã lớp', sortable: true },
  { key: 'title', label: 'Tên lớp', sortable: true },
  { key: 'subject', label: 'Môn học', sortable: true },
  { key: 'grade', label: 'Khối', sortable: true },
  { key: 'schoolYear', label: 'Năm học', sortable: true },
  { key: 'studentCount', label: 'Sĩ số', sortable: true },
  { key: 'status', label: 'Trạng thái', sortable: true },
  { key: 'actions', label: 'Thao tác', sortable: false }
] as const;
